import { useCallback, useEffect } from 'react';
import * as config from '../../../../../server/config';
import { invalidEmailMessage } from '../../../../../shared/errorMessages';
import { checkEmailUniqueness } from '../../../../services/checkEmailUniqueness';
import { useStateContext } from '../../../../modules/stateContext';
import { checkUserIdentifierUniqueness } from '../../../../services/checkUserIdentifierUniqueness';
import renderQueryString from '../../../../../shared/urls/renderQueryString';

declare const window: {
    AwsWafIntegration: {
        getToken: () => Promise<string>;
    };
} & Window;

type CheckEmailUniquenessReturnType = Awaited<
    ReturnType<typeof checkEmailUniqueness>
>;

type CheckEmailUniquenessReturnMessage = Exclude<
    CheckEmailUniquenessReturnType['message'],
    undefined
>;

type CheckUniquenessReturnValue = {
    dns?: boolean | null;
    error?: string | string[] | CheckEmailUniquenessReturnMessage;
    exists?: boolean;
};

async function execCheckUserIdentifierUniqueness(
    identifier: string,
    queryParams: string = '',
    wafSDK: boolean = false
) {
    let dns: CheckUniquenessReturnValue['dns'];

    let error: CheckUniquenessReturnValue['error'];

    let exists: CheckUniquenessReturnValue['exists'] = false;

    try {
        const token = wafSDK ? await window.AwsWafIntegration?.getToken?.() : undefined;

        ({ dns, exists, error } = await checkUserIdentifierUniqueness(
            identifier,
            queryParams,
            token
        ));

        if (typeof error !== 'undefined' && dns !== null) {
            throw new TypeError(String(error));
        }

        if (dns === null) {
            error = 'auth.singleEntry.u13Error.message';
        } else if (dns) {
            error = {
                email: {
                    id: 'emailFirst.email.duplicate',
                    attributes: [
                        {
                            href: '/auth',
                        },
                        {
                            href: '/signin/forgotten',
                        },
                    ],
                },
                federatedEmail: {
                    id: 'emailFirst.email.duplicate',
                    attributes: [
                        {
                            href: '/signin/federated',
                        },
                        {
                            href: '/signin/forgotten',
                        },
                    ],
                },
            };
        } else {
            error = invalidEmailMessage;
        }
    } catch {
        error = invalidEmailMessage;
    }

    return { dns, error, exists };
}

export function useEmailFirstEmailFormCheckUniqueness() {
    const userContext = useStateContext();
    const { isSingleEntryAuth, isFederated } = userContext;

    useEffect(() => {
        if (userContext.featureToggles?.wafSDK && isSingleEntryAuth) {
            const script = document.createElement('script');
            script.setAttribute('id', 'waf-sdk');
            const body = document.getElementsByTagName('body')[0];
            script.src = `${config.get(
                'wafIntelligentThreatSDKUrl'
            )}challenge.js`;
            body.appendChild(script);
        }
    }, []);

    const checkUniqueness = useCallback(
        async (identifier: string): Promise<CheckUniquenessReturnValue> => {
            try {
                if (isSingleEntryAuth) {
                    return execCheckUserIdentifierUniqueness(
                        identifier,
                        renderQueryString.call(userContext),
                        userContext.featureToggles?.wafSDK
                    );
                }

                const additionalContext = isFederated ? { isFederated } : {};

                const { message: error, success } = await checkEmailUniqueness(
                    identifier,
                    renderQueryString.call(userContext, additionalContext)
                );

                return { dns: undefined, error, exists: !success };
            } catch (error) {
                let errorMessage = 'unknown error';

                if (error instanceof Error) {
                    errorMessage = error.message;
                } else {
                    errorMessage = String(error);
                }

                return {
                    dns: undefined,
                    error: errorMessage,
                    exists: undefined,
                };
            }
        },
        [isSingleEntryAuth, userContext]
    );

    return checkUniqueness;
}
